import type { ReactNode } from 'react';
import NotFoundPage from '@/pages/not-found.page';
import { useGeoset } from '@/services/db/geoset/queries';
import { useGeosetIdFromCurrentUrl } from '@/services/router/use-geoset-id';

type GeosetRequiredGuardProps = {
  children: ReactNode;
};

/**
 * Gates the geoset-scoped geofence pages (index, create, import, edit) on a
 * real geoset. Those pages live under `/geosets/:geosetId/geofences/…` and read
 * the id straight from the URL, so a missing/non-numeric id or a geoset that
 * doesn't exist would otherwise render with an empty id and broken links. Here
 * we short-circuit to the not-found page instead:
 *
 * - No parsable geoset id in the URL → render {@link NotFoundPage} directly.
 * - Id present but no such geoset → the suspense read throws the API 404, which
 *   bubbles to the root not-found boundary (same path `useGeofence` takes for a
 *   missing geofence). The shared `LazyPage` Suspense covers the load.
 */
export function GeosetRequiredGuard({ children }: GeosetRequiredGuardProps) {
  const geosetId = useGeosetIdFromCurrentUrl();

  if (geosetId == null) {
    return <NotFoundPage />;
  }

  return <GeosetExistenceGuard geosetId={geosetId}>{children}</GeosetExistenceGuard>;
}

type GeosetExistenceGuardProps = {
  geosetId: string;
  children: ReactNode;
};

function GeosetExistenceGuard({ geosetId, children }: GeosetExistenceGuardProps) {
  // Read for its side effects: suspends while loading and throws a 404 for an
  // unknown geoset, which the root not-found boundary turns into NotFoundPage.
  useGeoset({ id: geosetId });
  return children;
}
